import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import Logo from './Logo';
import { programRoutes } from '../data/mock';

const links = [
  { to: '/', label: 'Home' },
  { to: '/about-us', label: 'About' },
  { to: '/programs', label: 'Programs', sub: true },
  { to: '/enroll', label: 'Enrollment' },
  { to: '/faq', label: 'FAQ' },
  { to: '/contact', label: 'Contact' },
  { to: '/book', label: 'Book' },
  { to: '/sponsor-a-lesson', label: 'Sponsor' },
  { to: '/blog', label: 'Blog', mobileOnly: true },
];

const desktopCls = ({ isActive }) =>
  `px-3 py-2 rounded-full text-sm font-medium text-white/95 hover:bg-white/15 transition-colors whitespace-nowrap ${isActive ? 'underline underline-offset-4 decoration-2 decoration-white' : ''}`;

function DesktopPrograms() {
  return (
    <div className="relative group">
      <NavLink to="/programs" className={(s) => `${desktopCls(s)} inline-flex items-center gap-1`} data-testid="nav-programs">
        Programs <ChevronDown className="w-3.5 h-3.5 group-hover:rotate-180 transition-transform" />
      </NavLink>
      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity">
        <div className="w-72 rounded-2xl bg-white border border-splash-sand shadow-xl p-2">
          {programRoutes.map((r) => (
            <Link key={r.id} to={r.to} data-testid={`nav-sub-${r.id}`} className="block rounded-xl px-4 py-3 hover:bg-splash-cream">
              <div className="font-semibold text-splash-green text-sm">{r.title}</div>
              <div className="text-xs text-splash-coral font-semibold">{r.ages}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobilePrograms() {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className="flex items-center">
        <NavLink to="/programs" end className={({ isActive }) => `flex-1 px-3 py-3 rounded-lg text-splash-green font-medium ${isActive ? 'bg-splash-teal/10' : ''}`}>Programs</NavLink>
        <button type="button" aria-label="Show programs by age" aria-expanded={open} onClick={() => setOpen((v) => !v)} data-testid="mobile-programs-toggle" className="p-3 rounded-lg text-splash-green hover:bg-splash-teal/10">
          <ChevronDown className={`w-5 h-5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <div className="ml-3 mb-2 border-l-2 border-splash-coral/40 pl-3 flex flex-col" data-testid="mobile-programs-submenu">
          {programRoutes.map((r) => (
            <Link key={r.id} to={r.to} data-testid={`mobile-sub-${r.id}`} className="px-3 py-2.5 rounded-lg hover:bg-splash-teal/10">
              <span className="block text-splash-green font-medium text-sm">{r.title}</span>
              <span className="block text-xs text-splash-coral font-semibold">{r.ages}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-50 w-full transition-colors ${scrolled ? 'bg-splash-cream/90 backdrop-blur' : 'bg-splash-cream'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-4">
        <Logo size="xl" />
        <nav className="hidden lg:flex items-center gap-1 rounded-full bg-splash-teal px-2 py-1.5 shadow-sm">
          {links.filter((l) => !l.mobileOnly).map((l) => (l.sub ? <DesktopPrograms key={l.to} /> : (
            <NavLink key={l.to} to={l.to} end={l.to === '/'} className={desktopCls}>{l.label}</NavLink>
          )))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/enroll" className="hidden md:inline-flex items-center rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-5 py-2.5 text-sm font-semibold btn-transition btn-focus">
            Enroll Now
          </Link>
          <button className="lg:hidden p-2 rounded-full bg-splash-teal text-white" aria-label="Toggle menu" data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-splash-sand bg-splash-cream max-h-[80vh] overflow-y-auto">
          <nav className="px-4 py-3 flex flex-col">
            {links.map((l) => (l.sub ? <MobilePrograms key={l.to} /> : (
              <NavLink key={l.to} to={l.to} end={l.to === '/'} className={({ isActive }) => `px-3 py-3 rounded-lg text-splash-green font-medium ${isActive ? 'bg-splash-teal/10' : ''}`}>
                {l.label}
              </NavLink>
            )))}
          </nav>
        </div>
      )}
    </header>
  );
}
